import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Users, DollarSign, FileText, Building2, TrendingUp, 
  UserPlus, Search, ChevronLeft, ChevronRight, LogOut,
  Eye, MoreVertical, RefreshCw, Calendar, CreditCard,
  AlertCircle, CheckCircle, XCircle, Clock, Download,
  Mail, Bell, Shield, BarChart3, Globe, Activity,
  MessageSquare, UserCog, TrendingDown, Target,
  Send, Trash2, UserX, UserCheck, List, Plus, Edit
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const adminAxios = axios.create({ baseURL: API });
adminAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Admin Login Component
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("admin_token", response.data.access_token);
      onLogin(response.data);
      toast.success("Welcome to Admin Portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email");
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const backToLogin = () => {
    setMode("login");
    setForgotSent(false);
    setForgotEmail("");
  };

  if (mode === "forgot") {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-800 border-slate-700">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-2xl font-bold">L</span>
            </div>
            <CardTitle className="text-2xl text-white">Reset Admin Password</CardTitle>
            <p className="text-slate-400 text-sm">We'll email you a reset link</p>
          </CardHeader>
          <CardContent>
            {forgotSent ? (
              <div className="text-center space-y-4">
                <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="text-slate-300 text-sm">
                  If an admin account exists for <strong>{forgotEmail}</strong>, a password reset link has been sent. The link expires in 1 hour.
                </p>
                <Button onClick={backToLogin} variant="outline" className="w-full border-slate-600 text-slate-300">
                  Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Admin Email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="admin-forgot-email-input"
                />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={forgotLoading} data-testid="admin-forgot-submit-btn">
                  {forgotLoading ? "Sending..." : "Send reset link"}
                </Button>
                <button
                  type="button"
                  onClick={backToLogin}
                  className="w-full text-center text-sm text-slate-400 hover:text-white"
                >
                  Back to sign in
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">L</span>
          </div>
          <CardTitle className="text-2xl text-white">Admin Portal</CardTitle>
          <p className="text-slate-400 text-sm">LedgerAMT Platform Management</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="email"
              placeholder="Admin Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              data-testid="admin-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
              data-testid="admin-password-input"
            />
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="admin-login-btn">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            <button
              type="button"
              onClick={() => setMode("forgot")}
              className="w-full text-center text-sm text-slate-400 hover:text-white"
              data-testid="admin-forgot-password-link"
            >
              Forgot password?
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Admin Reset Password Component (reached via /admin?reset_token=...)
function AdminResetPassword({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">L</span>
          </div>
          <CardTitle className="text-2xl text-white">Set New Password</CardTitle>
          <p className="text-slate-400 text-sm">Admin Portal password reset</p>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
              <p className="text-slate-300 text-sm">Your password has been reset. You can now sign in with your new password.</p>
              <Button onClick={onDone} className="w-full bg-primary">Sign in</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="New password (min. 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="admin-reset-password-input"
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="admin-reset-confirm-password-input"
              />
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading} data-testid="admin-reset-submit-btn">
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, icon: Icon, subtitle, color = "primary" }) {
  const colors = {
    primary: "bg-primary/10 text-primary",
    green: "bg-emerald-500/10 text-emerald-500",
    blue: "bg-blue-500/10 text-blue-500",
    amber: "bg-amber-500/10 text-amber-500",
    red: "bg-red-500/10 text-red-500"
  };

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-slate-400 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-white mt-2">{value}</p>
            {subtitle && <p className="text-slate-500 text-xs mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colors[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// User Detail Modal
function UserDetailModal({ user, isOpen, onClose, onUpdate, onSaveDetails, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.user?.name || "");
      setEmail(user.user?.email || "");
      setBusinessName(user.business_profile?.business_name || "");
      setNewPassword("");
      setEditing(false);
    }
  }, [user]);

  if (!user) return null;

  const handleSave = async () => {
    if (newPassword && newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    const payload = { name, email, business_name: businessName };
    if (newPassword) payload.new_password = newPassword;
    await onSaveDetails(user.user?.id, payload);
    setSaving(false);
    setEditing(false);
    setNewPassword("");
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: "bg-emerald-500/20", text: "text-emerald-400", icon: CheckCircle },
      cancelled: { bg: "bg-red-500/20", text: "text-red-400", icon: XCircle },
      expired: { bg: "bg-amber-500/20", text: "text-amber-400", icon: Clock },
      suspended: { bg: "bg-red-500/20", text: "text-red-400", icon: UserX },
    };
    const badge = badges[status] || badges.expired;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {status || "No subscription"}
      </span>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">User Details</DialogTitle>
          <DialogDescription className="text-slate-400">
            Manage user account and subscription
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {editing ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm">Email</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div>
                <label className="text-slate-400 text-sm">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-slate-400 text-sm">Business Name</label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="bg-slate-700 border-slate-600 text-white mt-1" />
              </div>
              <div className="col-span-2">
                <label className="text-slate-400 text-sm">New Password (leave blank to keep unchanged)</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  data-testid="user-new-password-input"
                />
              </div>
              <div className="col-span-2 flex gap-3">
                <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-user-details-btn">
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-600 text-slate-300">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-slate-400 text-sm">Email</p>
                <p className="text-white font-medium">{user.user?.email}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Name</p>
                <p className="text-white font-medium">{user.user?.name || "Not set"}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Business Name</p>
                <p className="text-white font-medium">{user.business_profile?.business_name || "Not set"}</p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Joined</p>
                <p className="text-white font-medium">
                  {user.user?.created_at ? new Date(user.user.created_at).toLocaleDateString() : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-slate-400 text-sm">User Status</p>
                <p className="text-white font-medium capitalize">{user.user?.status || "active"}</p>
              </div>
              {canEdit && (
                <div className="flex items-end">
                  <Button variant="outline" onClick={() => setEditing(true)} className="border-slate-600 text-slate-300" data-testid="edit-user-details-btn">
                    <Edit className="w-4 h-4 mr-2" /> Edit Details
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Subscription</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-slate-400 text-xs">Status</p>
                {getStatusBadge(user.subscription?.status)}
              </div>
              <div>
                <p className="text-slate-400 text-xs">Plan</p>
                <p className="text-white capitalize">{user.subscription?.plan || "None"}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Amount</p>
                <p className="text-white">${user.subscription?.amount || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-700/50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Documents Created</h4>
            <div className="flex gap-4 flex-wrap">
              {Object.entries(user.document_stats || {}).map(([type, count]) => (
                <div key={type} className="bg-slate-600/50 px-3 py-2 rounded-lg">
                  <p className="text-xs text-slate-400 capitalize">{type.replace("-", " ")}</p>
                  <p className="text-lg font-semibold text-white">{count}</p>
                </div>
              ))}
              {Object.keys(user.document_stats || {}).length === 0 && (
                <p className="text-slate-500 text-sm">No documents created</p>
              )}
            </div>
          </div>

          {canEdit ? (
            <div className="flex gap-3 pt-4 border-t border-slate-700 flex-wrap">
              <Button
                variant="outline"
                className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                onClick={() => onUpdate(user.user?.id, "suspended")}
                data-testid="suspend-user-btn"
              >
                <UserX className="w-4 h-4 mr-2" /> Suspend User
              </Button>
              <Button
                variant="outline"
                className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                onClick={() => onUpdate(user.user?.id, "active")}
                data-testid="activate-user-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" /> Activate User
              </Button>
            </div>
          ) : (
            <p className="text-slate-500 text-sm pt-4 border-t border-slate-700">
              Your role does not have permission to modify users.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Announcement Modal
function AnnouncementModal({ isOpen, onClose, onSubmit }) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [type, setType] = useState("info");

  const handleSubmit = () => {
    onSubmit({ title, content, type });
    setTitle("");
    setContent("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Create Announcement</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-slate-700 border-slate-600 text-white" />
          <Textarea placeholder="Content" value={content} onChange={(e) => setContent(e.target.value)} className="bg-slate-700 border-slate-600 text-white min-h-[100px]" />
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSubmit} className="w-full bg-primary">Create Announcement</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Email Broadcast Modal
function EmailBroadcastModal({ isOpen, onClose, onSubmit }) {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [target, setTarget] = useState("all");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit({ subject, content, target });
    setLoading(false);
    setSubject("");
    setContent("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Email Broadcast</DialogTitle>
          <DialogDescription className="text-slate-400">Send email to multiple users</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
              <SelectValue placeholder="Select target" />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="active">Active Subscribers</SelectItem>
              <SelectItem value="inactive">Inactive Users</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-slate-700 border-slate-600 text-white" />
          <Textarea placeholder="Email content (use {{name}} for personalization)" value={content} onChange={(e) => setContent(e.target.value)} className="bg-slate-700 border-slate-600 text-white min-h-[150px]" />
          <Button onClick={handleSubmit} className="w-full bg-primary" disabled={loading}>
            {loading ? "Sending..." : "Send Broadcast"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Support Ticket Response Modal
function TicketResponseModal({ ticket, isOpen, onClose, onSubmit }) {
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState(ticket?.status || "open");

  const handleSubmit = () => {
    onSubmit(ticket.id, { message, status });
    setMessage("");
    onClose();
  };

  if (!ticket) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Respond to Ticket</DialogTitle>
          <DialogDescription className="text-slate-400">{ticket.subject}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-700/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">Original Message:</p>
            <p className="text-sm text-white mt-1">{ticket.message}</p>
          </div>
          <Textarea placeholder="Your response..." value={message} onChange={(e) => setMessage(e.target.value)} className="bg-slate-700 border-slate-600 text-white min-h-[100px]" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleSubmit} className="w-full bg-primary">Send Response</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Add Admin Team Member Modal
function AddAdminModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name || !email || !password) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    await onSubmit({ name, email, password, role });
    setLoading(false);
    setName("");
    setEmail("");
    setPassword("");
    setRole("admin");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Add Admin Team Member</DialogTitle>
          <DialogDescription className="text-slate-400">
            Add a new administrator to manage the platform
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400">Full Name</label>
            <Input 
              placeholder="John Doe" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              className="bg-slate-700 border-slate-600 text-white mt-1" 
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Email</label>
            <Input 
              type="email"
              placeholder="admin@company.com" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              className="bg-slate-700 border-slate-600 text-white mt-1" 
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Password</label>
            <Input 
              type="password"
              placeholder="Secure password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              className="bg-slate-700 border-slate-600 text-white mt-1" 
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Role</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="admin">Admin - Full platform management</SelectItem>
                <SelectItem value="support">Support - Tickets & user assistance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-3">
            <p className="text-xs text-slate-400">The new admin will receive an email with their login credentials.</p>
          </div>
          <Button onClick={handleSubmit} className="w-full bg-primary" disabled={loading}>
            {loading ? "Adding..." : "Add Team Member"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edit Admin Team Member Modal
function EditAdminModal({ adminUser, isOpen, onClose, onUpdate, onActivate, onDeactivate, onDelete }) {
  const [name, setName] = useState(adminUser?.name || "");
  const [role, setRole] = useState(adminUser?.role || "admin");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (adminUser) {
      setName(adminUser.name || "");
      setRole(adminUser.role || "admin");
    }
  }, [adminUser]);

  if (!adminUser) return null;

  const isPrimary = adminUser.id === "primary" || adminUser.is_primary;

  const handleUpdate = async () => {
    setLoading(true);
    await onUpdate(adminUser.id, { name, role });
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Manage Admin: {adminUser.name || adminUser.email}</DialogTitle>
          <DialogDescription className="text-slate-400">
            Update role, status, or remove this admin
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-700/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">Email</p>
                <p className="text-white">{adminUser.email}</p>
              </div>
              <div>
                <p className="text-slate-400">Status</p>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  adminUser.active !== false ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                }`}>
                  {adminUser.active !== false ? "Active" : "Deactivated"}
                </span>
              </div>
              <div>
                <p className="text-slate-400">Current Role</p>
                <p className="text-white capitalize">{adminUser.role?.replace("_", " ")}</p>
              </div>
              <div>
                <p className="text-slate-400">Created</p>
                <p className="text-white">{adminUser.created_at ? new Date(adminUser.created_at).toLocaleDateString() : "N/A"}</p>
              </div>
            </div>
          </div>

          {!isPrimary && (
            <>
              <div>
                <label className="text-sm text-slate-400">Name</label>
                <Input 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  className="bg-slate-700 border-slate-600 text-white mt-1" 
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Role</label>
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="admin">Admin - Full platform management</SelectItem>
                    <SelectItem value="support">Support - Tickets & user assistance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdate} className="w-full bg-primary" disabled={loading}>
                {loading ? "Updating..." : "Update Admin"}
              </Button>
              
              <div className="border-t border-slate-700 pt-4 space-y-2">
                <p className="text-sm text-slate-400">Quick Actions</p>
                <div className="flex gap-2">
                  {adminUser.active !== false ? (
                    <Button 
                      variant="outline" 
                      className="flex-1 border-amber-500 text-amber-500 hover:bg-amber-500/10"
                      onClick={() => { onDeactivate(adminUser.id); onClose(); }}
                    >
                      <UserX className="w-4 h-4 mr-2" /> Deactivate
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="flex-1 border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                      onClick={() => { onActivate(adminUser.id); onClose(); }}
                    >
                      <UserCheck className="w-4 h-4 mr-2" /> Activate
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10"
                    onClick={() => { onDelete(adminUser.id); onClose(); }}
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Remove
                  </Button>
                </div>
              </div>
            </>
          )}
          
          {isPrimary && (
            <div className="bg-amber-500/10 text-amber-400 p-3 rounded-lg text-sm">
              Primary admin cannot be modified or removed.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Create User Modal
function CreateUserModal({ isOpen, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email || !password) {
      toast.error("Email and password are required");
      return;
    }
    setLoading(true);
    await onSubmit({ name, email, password });
    setLoading(false);
    setName("");
    setEmail("");
    setPassword("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a platform user account directly (no subscription required)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400">Full Name</label>
            <Input
              placeholder="Jane Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white mt-1"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Email *</label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white mt-1"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400">Password *</label>
            <Input
              type="password"
              placeholder="Secure password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white mt-1"
            />
          </div>
          <Button onClick={handleSubmit} className="w-full bg-primary" disabled={loading}>
            {loading ? "Creating..." : "Create User"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main Admin Component
export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get("reset_token");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [churnData, setChurnData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [docAnalytics, setDocAnalytics] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [failedLogins, setFailedLogins] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [adminTeam, setAdminTeam] = useState([]);
  const [userActivity, setUserActivity] = useState(null);
  
  // UI states
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [addAdminModalOpen, setAddAdminModalOpen] = useState(false);
  const [editAdminModalOpen, setEditAdminModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);

  const canManageUsers = adminUser?.role === "super_admin" || adminUser?.role === "admin";

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) verifyAdmin();
    else setLoading(false);
  }, []);

  const verifyAdmin = async () => {
    try {
      const response = await adminAxios.get("/admin/me");
      setAdminUser(response.data);
      setIsAuthenticated(true);
      loadDashboardData();
    } catch (error) {
      localStorage.removeItem("admin_token");
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, chartRes] = await Promise.all([
        adminAxios.get("/admin/dashboard/stats"),
        adminAxios.get("/admin/users", { params: { page: 1, limit: 10 } }),
        adminAxios.get("/admin/revenue/chart", { params: { period: "6months" } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setTotalPages(usersRes.data.total_pages);
      setChartData(chartRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      const [churn, forecast, engagement, geo, docs, funnel] = await Promise.all([
        adminAxios.get("/admin/analytics/churn"),
        adminAxios.get("/admin/analytics/revenue-forecast"),
        adminAxios.get("/admin/analytics/engagement"),
        adminAxios.get("/admin/analytics/geography"),
        adminAxios.get("/admin/analytics/documents"),
        adminAxios.get("/admin/analytics/funnel")
      ]);
      setChurnData(churn.data);
      setForecastData(forecast.data);
      setEngagementData(engagement.data);
      setGeoData(geo.data);
      setDocAnalytics(docs.data);
      setFunnelData(funnel.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    }
  };

  const loadSecurityData = async () => {
    try {
      const [logs, failed, team] = await Promise.all([
        adminAxios.get("/admin/activity-logs", { params: { limit: 50 } }),
        adminAxios.get("/admin/security/failed-logins"),
        adminAxios.get("/admin/team")
      ]);
      setActivityLogs(logs.data.logs);
      setFailedLogins(failed.data);
      setAdminTeam(team.data);
    } catch (error) {
      toast.error("Failed to load security data");
    }
  };

  const loadSupportData = useCallback(async () => {
    try {
      const [ticketsRes, announcementsRes] = await Promise.all([
        adminAxios.get("/admin/support/tickets"),
        adminAxios.get("/admin/announcements")
      ]);
      setTickets(ticketsRes.data.tickets || []);
      setAnnouncements(announcementsRes.data || []);
    } catch (error) {
      toast.error("Failed to load support data");
    }
  }, []);

  const loadUserActivityData = async () => {
    try {
      const response = await adminAxios.get("/admin/user-activity", { params: { limit: 100 } });
      setUserActivity(response.data);
    } catch (error) {
      toast.error("Failed to load user activity");
    }
  };

  // Real-time polling for support tickets (every 10 seconds when on support tab)
  useEffect(() => {
    let interval;
    if (isAuthenticated && activeTab === "support") {
      loadSupportData();
      interval = setInterval(loadSupportData, 10000); // Poll every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, isAuthenticated, loadSupportData]);

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === "analytics") loadAnalyticsData();
      else if (activeTab === "security") loadSecurityData();
      else if (activeTab === "activity") loadUserActivityData();
    }
  }, [activeTab, isAuthenticated]);

  const handleLogin = (data) => {
    setAdminUser(data);
    setIsAuthenticated(true);
    loadDashboardData();
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const handleSearch = async () => {
    try {
      const response = await adminAxios.get("/admin/users", { params: { page: 1, limit: 10, search: searchQuery } });
      setUsers(response.data.users);
      setTotalPages(response.data.total_pages);
      setCurrentPage(1);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const loadUserDetail = async (userId) => {
    try {
      const response = await adminAxios.get(`/admin/users/${userId}`);
      setSelectedUser(response.data);
      setUserDetailOpen(true);
    } catch (error) {
      toast.error("Failed to load user");
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await adminAxios.put(`/admin/users/${userId}`, { status });
      toast.success(`User ${status === "active" ? "activated" : "suspended"}`);
      setUserDetailOpen(false);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const updateUserDetails = async (userId, details) => {
    try {
      const response = await adminAxios.put(`/admin/users/${userId}`, details);
      if (details.new_password) {
        toast.success(response.data.email_sent ? "User details updated. Password change email sent." : "User details updated. Password changed, but the notification email failed to send.");
      } else {
        toast.success("User details updated");
      }
      loadUserDetail(userId);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user details");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) {
      toast.error("Select users first");
      return;
    }
    try {
      const response = await adminAxios.post("/admin/users/bulk-action", { user_ids: selectedUsers, action });
      toast.success(`${response.data.success} users updated`);
      setSelectedUsers([]);
      loadDashboardData();
    } catch (error) {
      toast.error("Bulk action failed");
    }
  };

  const createUser = async (data) => {
    try {
      await adminAxios.post("/admin/users/create", data);
      toast.success(`User ${data.email} created successfully`);
      loadDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const exportUsers = async () => {
    try {
      const response = await adminAxios.get("/admin/users/export", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const createAnnouncement = async (data) => {
    try {
      await adminAxios.post("/admin/announcements", data);
      toast.success("Announcement created");
      loadSupportData();
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await adminAxios.delete(`/admin/announcements/${id}`);
      toast.success("Announcement deleted");
      loadSupportData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const sendEmailBroadcast = async (data) => {
    try {
      const response = await adminAxios.post("/admin/email/broadcast", data);
      toast.success(`Sent to ${response.data.sent} users`);
    } catch (error) {
      toast.error("Broadcast failed");
    }
  };

  const respondToTicket = async (ticketId, data) => {
    try {
      await adminAxios.post(`/admin/support/tickets/${ticketId}/respond`, data);
      toast.success("Response sent");
      loadSupportData();
    } catch (error) {
      toast.error("Failed to respond");
    }
  };

  // Admin Team Management Functions
  const addAdminTeamMember = async (data) => {
    try {
      const response = await adminAxios.post("/admin/team/create", data);
      toast.success(`Admin created. ${response.data.email_sent ? 'Email sent.' : ''}`);
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    }
  };

  const updateAdminTeamMember = async (adminId, data) => {
    try {
      const response = await adminAxios.put(`/admin/team/${adminId}`, data);
      toast.success(`Admin updated. ${response.data.email_sent ? 'Notification sent.' : ''}`);
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update admin");
    }
  };

  const activateAdminTeamMember = async (adminId) => {
    try {
      const response = await adminAxios.post(`/admin/team/${adminId}/activate`);
      toast.success(`Admin activated. ${response.data.email_sent ? 'Email sent.' : ''}`);
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate admin");
    }
  };

  const deactivateAdminTeamMember = async (adminId) => {
    try {
      const response = await adminAxios.post(`/admin/team/${adminId}/deactivate`);
      toast.success(`Admin deactivated. ${response.data.email_sent ? 'Email sent.' : ''}`);
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate admin");
    }
  };

  const deleteAdminTeamMember = async (adminId) => {
    if (!window.confirm("Are you sure you want to remove this admin? This action cannot be undone.")) {
      return;
    }
    try {
      const response = await adminAxios.delete(`/admin/team/${adminId}`);
      toast.success(`Admin removed. ${response.data.email_sent ? 'Notification sent.' : ''}`);
      loadSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove admin");
    }
  };

  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    try {
      const response = await adminAxios.get("/admin/users", { params: { page: newPage, limit: 10, search: searchQuery } });
      setUsers(response.data.users);
    } catch (error) {
      toast.error("Failed to load page");
    }
  };

  if (loading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated && resetToken) {
    return <AdminResetPassword token={resetToken} onDone={() => setSearchParams({})} />;
  }

  if (!isAuthenticated) return <AdminLogin onLogin={handleLogin} />;

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "users", label: "Users", icon: Users },
    { id: "activity", label: "User Activity", icon: Activity },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "support", label: "Support", icon: MessageSquare },
    { id: "security", label: "Security", icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">L</span>
            </div>
            <div>
              <h1 className="text-white font-semibold">LedgerAMT Admin</h1>
              <p className="text-slate-400 text-xs">Platform Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">{adminUser?.email}</span>
            <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full">{adminUser?.role || "admin"}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id ? "text-primary border-b-2 border-primary" : "text-slate-400 hover:text-white"
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* OVERVIEW TAB */}
            {activeTab === "overview" && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard title="Total Users" value={stats?.users?.total || 0} icon={Users} subtitle={`${stats?.users?.new_today || 0} new today`} color="primary" />
                  <StatCard title="Active Subscriptions" value={stats?.users?.active || 0} icon={CheckCircle} subtitle={`${stats?.users?.cancelled || 0} cancelled`} color="green" />
                  <StatCard title="Monthly Revenue (MRR)" value={`$${(stats?.revenue?.mrr || 0).toFixed(2)}`} icon={DollarSign} color="blue" />
                  <StatCard title="Total Documents" value={stats?.platform?.total_documents || 0} icon={FileText} color="amber" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">User Growth</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                            <Area type="monotone" dataKey="new_users" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="New Users" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white text-lg">Subscription Trend</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                            <Bar dataKey="new_subscriptions" fill="#3b82f6" name="New Subscriptions" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm"><UserPlus className="w-4 h-4" />New This Month</div>
                    <p className="text-2xl font-bold text-white mt-1">{stats?.users?.new_this_month || 0}</p>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm"><Clock className="w-4 h-4" />Expired</div>
                    <p className="text-2xl font-bold text-amber-400 mt-1">{stats?.users?.expired || 0}</p>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm"><CreditCard className="w-4 h-4" />Monthly Plans</div>
                    <p className="text-2xl font-bold text-white mt-1">{stats?.revenue?.monthly_subscriptions || 0}</p>
                  </div>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-slate-400 text-sm"><Calendar className="w-4 h-4" />Yearly Plans</div>
                    <p className="text-2xl font-bold text-white mt-1">{stats?.revenue?.yearly_subscriptions || 0}</p>
                  </div>
                </div>
              </div>
            )}

            {/* USERS TAB */}
            {activeTab === "users" && (
              <div className="space-y-6">
                <div className="flex gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by email or name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                      className="pl-10 bg-slate-800 border-slate-700 text-white"
                      data-testid="admin-user-search"
                    />
                  </div>
                  <Button onClick={handleSearch} className="bg-primary">Search</Button>
                  <Button variant="outline" onClick={loadDashboardData} className="border-slate-600 text-slate-300"><RefreshCw className="w-4 h-4" /></Button>
                  <Button variant="outline" onClick={exportUsers} className="border-slate-600 text-slate-300"><Download className="w-4 h-4 mr-2" />Export CSV</Button>
                  <Button onClick={() => setCreateUserModalOpen(true)} className="bg-emerald-600 hover:bg-emerald-700"><UserPlus className="w-4 h-4 mr-2" />Create User</Button>
                </div>

                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-4 p-3 bg-slate-800 rounded-lg">
                    <span className="text-slate-300 text-sm">{selectedUsers.length} selected</span>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction("suspend")} className="border-amber-500 text-amber-500">Suspend All</Button>
                    <Button size="sm" variant="outline" onClick={() => handleBulkAction("activate")} className="border-emerald-500 text-emerald-500">Activate All</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUsers([])} className="text-slate-400">Clear</Button>
                  </div>
                )}

                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">
                            <input type="checkbox" onChange={(e) => setSelectedUsers(e.target.checked ? users.map(u => u.id) : [])} className="rounded" />
                          </th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">User</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Business</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Subscription</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Docs</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Joined</th>
                          <th className="text-right p-4 text-slate-400 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-4">
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => setSelectedUsers(e.target.checked ? [...selectedUsers, user.id] : selectedUsers.filter(id => id !== user.id))}
                                className="rounded"
                              />
                            </td>
                            <td className="p-4">
                              <p className="text-white font-medium">{user.name || "No name"}</p>
                              <p className="text-slate-400 text-sm">{user.email}</p>
                            </td>
                            <td className="p-4">
                              <p className="text-white">{user.business_name || "Not set"}</p>
                            </td>
                            <td className="p-4">
                              {user.subscription ? (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                  user.subscription.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"
                                }`}>
                                  {user.subscription.status} - {user.subscription.plan}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-sm">None</span>
                              )}
                            </td>
                            <td className="p-4 text-white">{user.document_count || 0}</td>
                            <td className="p-4 text-slate-400 text-sm">{new Date(user.created_at).toLocaleDateString()}</td>
                            <td className="p-4 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="text-slate-400"><MoreVertical className="w-4 h-4" /></Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                                  <DropdownMenuItem onClick={() => loadUserDetail(user.id)} className="text-white hover:bg-slate-700 cursor-pointer">
                                    <Eye className="w-4 h-4 mr-2" />View Details
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="border-slate-700">
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-slate-400 text-sm">Page {currentPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="border-slate-700">
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* USER ACTIVITY TAB */}
            {activeTab === "activity" && (
              <div className="space-y-6">
                {userActivity && (
                  <>
                    {/* Activity Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="p-6">
                          <p className="text-slate-400 text-sm">Total Activities</p>
                          <p className="text-3xl font-bold text-white">{userActivity.total || 0}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="p-6">
                          <p className="text-slate-400 text-sm">Activity Types</p>
                          <p className="text-3xl font-bold text-white">{userActivity.activity_breakdown?.length || 0}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800 border-slate-700">
                        <CardContent className="p-6">
                          <p className="text-slate-400 text-sm">Active Users</p>
                          <p className="text-3xl font-bold text-white">{userActivity.most_active_users?.length || 0}</p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Activity Breakdown */}
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white">Activity Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {userActivity.activity_breakdown?.slice(0, 10).map((a) => (
                            <div key={a.action} className="flex justify-between items-center p-2 bg-slate-700/50 rounded">
                              <span className="text-white text-sm capitalize">{a.action?.replace(/_/g, ' ')}</span>
                              <span className="text-primary font-semibold">{a.count}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>

                      {/* Most Active Users */}
                      <Card className="bg-slate-800 border-slate-700">
                        <CardHeader>
                          <CardTitle className="text-white">Most Active Users</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {userActivity.most_active_users?.slice(0, 10).map((u) => (
                            <div key={u.user_id} className="flex justify-between items-center p-2 bg-slate-700/50 rounded">
                              <span className="text-white text-sm">{u.email}</span>
                              <span className="text-emerald-400 font-semibold">{u.actions} actions</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Recent Activity Log */}
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-white">Recent Platform Activity</CardTitle>
                        <Button variant="outline" size="sm" onClick={loadUserActivityData} className="border-slate-600 text-slate-300">
                          <RefreshCw className="w-4 h-4 mr-2" />Refresh
                        </Button>
                      </CardHeader>
                      <CardContent className="p-0">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left p-4 text-slate-400 font-medium text-sm">User</th>
                              <th className="text-left p-4 text-slate-400 font-medium text-sm">Action</th>
                              <th className="text-left p-4 text-slate-400 font-medium text-sm">Resource</th>
                              <th className="text-left p-4 text-slate-400 font-medium text-sm">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userActivity.logs?.slice(0, 20).map((log) => (
                              <tr key={log.id} className="border-b border-slate-700/50">
                                <td className="p-4">
                                  <p className="text-white text-sm">{log.user_email || 'Unknown'}</p>
                                </td>
                                <td className="p-4">
                                  <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded capitalize">
                                    {log.action?.replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td className="p-4 text-slate-400 text-sm capitalize">{log.resource_type}</td>
                                <td className="p-4 text-slate-500 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  </>
                )}
                {!userActivity && (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                  </div>
                )}
              </div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === "analytics" && (
              <div className="space-y-8">
                {/* Conversion Funnel */}
                {funnelData && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2"><Target className="w-5 h-5" />Conversion Funnel</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                        {funnelData.funnel?.map((stage, i) => (
                          <div key={stage.stage} className="text-center">
                            <div className={`h-24 rounded-lg flex items-end justify-center p-2 ${i === 0 ? 'bg-primary/20' : 'bg-slate-700'}`}
                              style={{ height: `${Math.max(40, stage.percentage)}px` }}>
                              <span className="text-2xl font-bold text-white">{stage.count}</span>
                            </div>
                            <p className="text-xs text-slate-400 mt-2">{stage.stage}</p>
                            <p className="text-xs text-primary">{stage.percentage}%</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 text-center">
                        <span className="text-slate-400">Overall Conversion Rate: </span>
                        <span className="text-primary font-bold">{funnelData.conversion_rate}%</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Churn Analysis */}
                  {churnData && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><TrendingDown className="w-5 h-5 text-red-400" />Churn Analysis</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <p className="text-slate-400 text-sm">Overall Churn Rate</p>
                          <p className="text-3xl font-bold text-red-400">{churnData.overall_churn_rate}%</p>
                        </div>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={churnData.churn_by_month}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                              <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                              <Line type="monotone" dataKey="churn_rate" stroke="#ef4444" name="Churn %" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Revenue Forecast */}
                  {forecastData && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" />Revenue Forecast</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-slate-400 text-sm">Current MRR</p>
                            <p className="text-2xl font-bold text-emerald-400">${forecastData.current_mrr}</p>
                          </div>
                          <div>
                            <p className="text-slate-400 text-sm">Avg Growth/Month</p>
                            <p className="text-2xl font-bold text-white">{forecastData.avg_monthly_growth}</p>
                          </div>
                        </div>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={forecastData.forecast}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                              <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }} />
                              <Area type="monotone" dataKey="projected_mrr" stroke="#10b981" fill="#10b981" fillOpacity={0.2} name="Projected MRR" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* User Engagement */}
                  {engagementData && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><Activity className="w-5 h-5 text-blue-400" />User Engagement</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-white">{engagementData.active_users?.daily || 0}</p>
                            <p className="text-xs text-slate-400">DAU</p>
                          </div>
                          <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-white">{engagementData.active_users?.weekly || 0}</p>
                            <p className="text-xs text-slate-400">WAU</p>
                          </div>
                          <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                            <p className="text-2xl font-bold text-white">{engagementData.active_users?.monthly || 0}</p>
                            <p className="text-xs text-slate-400">MAU</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className="text-slate-400 text-sm">Top Features</p>
                          {engagementData.feature_usage?.slice(0, 5).map((f) => (
                            <div key={f.feature} className="flex justify-between items-center">
                              <span className="text-white text-sm capitalize">{f.feature?.replace(/_/g, ' ')}</span>
                              <span className="text-slate-400">{f.count}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Geographic Distribution */}
                  {geoData && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><Globe className="w-5 h-5 text-cyan-400" />Geographic Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-400 text-sm mb-4">{geoData.total_countries} countries</p>
                        <div className="space-y-2">
                          {geoData.distribution?.slice(0, 8).map((d) => (
                            <div key={d.country} className="flex justify-between items-center">
                              <span className="text-white text-sm">{d.country || "Unknown"}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-24 bg-slate-700 rounded-full h-2">
                                  <div className="bg-cyan-400 h-2 rounded-full" style={{ width: `${Math.min(100, (d.users / (geoData.distribution[0]?.users || 1)) * 100)}%` }}></div>
                                </div>
                                <span className="text-slate-400 text-sm w-8">{d.users}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Document Analytics */}
                {docAnalytics && (
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2"><FileText className="w-5 h-5 text-amber-400" />Document Analytics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-slate-400 text-sm mb-3">By Type</p>
                          <div className="space-y-2">
                            {docAnalytics.by_type?.map((d) => (
                              <div key={d.type} className="flex justify-between items-center">
                                <span className="text-white text-sm capitalize">{d.type?.replace(/-/g, ' ')}</span>
                                <span className="text-slate-400">{d.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-slate-400 text-sm mb-3">Monthly Trend</p>
                          <div className="h-32">
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={docAnalytics.by_month}>
                                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                <Bar dataKey="count" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* SUPPORT TAB */}
            {activeTab === "support" && (
              <div className="space-y-6">
                <div className="flex gap-4 flex-wrap">
                  <Button onClick={() => setAnnouncementModalOpen(true)} className="bg-primary"><Bell className="w-4 h-4 mr-2" />New Announcement</Button>
                  <Button onClick={() => setEmailModalOpen(true)} variant="outline" className="border-slate-600 text-slate-300"><Mail className="w-4 h-4 mr-2" />Email Broadcast</Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Support Tickets */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Support Tickets</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                      {tickets.length === 0 ? (
                        <p className="text-slate-500 text-center py-4">No tickets</p>
                      ) : (
                        tickets.map((ticket) => (
                          <div key={ticket.id} className="bg-slate-700/50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-white font-medium">{ticket.subject}</p>
                                <p className="text-slate-400 text-xs">{ticket.user_email}</p>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                ticket.status === "open" ? "bg-red-500/20 text-red-400" :
                                ticket.status === "in_progress" ? "bg-amber-500/20 text-amber-400" :
                                "bg-emerald-500/20 text-emerald-400"
                              }`}>{ticket.status}</span>
                            </div>
                            <p className="text-slate-300 text-sm mt-2 line-clamp-2">{ticket.message}</p>
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedTicket(ticket); setTicketModalOpen(true); }} className="text-primary mt-2">
                              Respond
                            </Button>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>

                  {/* Announcements */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">Announcements</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                      {announcements.length === 0 ? (
                        <p className="text-slate-500 text-center py-4">No announcements</p>
                      ) : (
                        announcements.map((ann) => (
                          <div key={ann.id} className="bg-slate-700/50 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <p className="text-white font-medium">{ann.title}</p>
                              <Button size="sm" variant="ghost" onClick={() => deleteAnnouncement(ann.id)} className="text-red-400 h-6 w-6 p-0">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            <p className="text-slate-300 text-sm mt-1">{ann.content}</p>
                            <p className="text-slate-500 text-xs mt-2">{new Date(ann.created_at).toLocaleDateString()}</p>
                          </div>
                        ))
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* SECURITY TAB */}
            {activeTab === "security" && (
              <div className="space-y-6">
                {/* Admin Team Management */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <UserCog className="w-5 h-5 text-purple-400" />Admin Team Management
                    </CardTitle>
                    <Button 
                      onClick={() => setAddAdminModalOpen(true)} 
                      className="bg-primary hover:bg-primary/90"
                      data-testid="add-admin-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Team Member
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {adminTeam.map((admin) => (
                        <div key={admin.id} className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700/70 transition-colors">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              admin.role === "super_admin" ? "bg-purple-500/20" : 
                              admin.role === "admin" ? "bg-blue-500/20" : "bg-emerald-500/20"
                            }`}>
                              <span className={`text-lg font-bold ${
                                admin.role === "super_admin" ? "text-purple-400" : 
                                admin.role === "admin" ? "text-blue-400" : "text-emerald-400"
                              }`}>{(admin.name || admin.email)[0].toUpperCase()}</span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{admin.name || "No name"}</p>
                              <p className="text-slate-400 text-sm">{admin.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs px-3 py-1.5 rounded-full ${
                              admin.role === "super_admin" ? "bg-purple-500/20 text-purple-400" : 
                              admin.role === "admin" ? "bg-blue-500/20 text-blue-400" : 
                              "bg-emerald-500/20 text-emerald-400"
                            }`}>
                              {admin.role?.replace("_", " ")}
                            </span>
                            {admin.active === false && (
                              <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400">Deactivated</span>
                            )}
                            {!admin.is_primary && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setSelectedAdmin(admin); setEditAdminModalOpen(true); }}
                                className="text-slate-400 hover:text-white"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                            {admin.is_primary && (
                              <span className="text-xs text-slate-500 italic">Primary</span>
                            )}
                          </div>
                        </div>
                      ))}
                      {adminTeam.length === 0 && (
                        <p className="text-slate-500 text-center py-4">No admin users</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Failed Logins */}
                  {failedLogins && (
                    <Card className="bg-slate-800 border-slate-700">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2"><AlertCircle className="w-5 h-5 text-red-400" />Failed Login Attempts</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-400 text-sm mb-4">Total: {failedLogins.total}</p>
                        {failedLogins.suspicious_emails?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-red-400 text-sm font-medium mb-2">Suspicious Activity</p>
                            {failedLogins.suspicious_emails.map((s) => (
                              <div key={s.email} className="flex justify-between items-center py-2 border-b border-slate-700">
                                <span className="text-white text-sm">{s.email}</span>
                                <span className="text-red-400">{s.attempts} attempts</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {failedLogins.attempts?.slice(0, 10).map((a) => (
                            <div key={a.id} className="text-sm flex justify-between">
                              <span className="text-slate-300">{a.email}</span>
                              <span className="text-slate-500">{new Date(a.timestamp).toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Role Permissions Info */}
                  <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2"><Shield className="w-5 h-5 text-amber-400" />Role Permissions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-3 bg-purple-500/10 rounded-lg">
                        <p className="text-purple-400 font-medium">Super Admin</p>
                        <p className="text-slate-400 text-sm">Full platform access, can manage all admin users</p>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded-lg">
                        <p className="text-blue-400 font-medium">Admin</p>
                        <p className="text-slate-400 text-sm">Platform management, cannot manage other admins</p>
                      </div>
                      <div className="p-3 bg-emerald-500/10 rounded-lg">
                        <p className="text-emerald-400 font-medium">Support</p>
                        <p className="text-slate-400 text-sm">Support tickets and user assistance only</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Activity Logs */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Admin Activity Logs</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Admin</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Action</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Details</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLogs.slice(0, 20).map((log) => (
                          <tr key={log.id} className="border-b border-slate-700/50">
                            <td className="p-4 text-white text-sm">{log.admin_email}</td>
                            <td className="p-4">
                              <span className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">{log.action}</span>
                            </td>
                            <td className="p-4 text-slate-400 text-sm">{log.target_user_id || log.target_email || "-"}</td>
                            <td className="p-4 text-slate-500 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
      </main>

      {/* Modals */}
      <UserDetailModal
        user={selectedUser}
        isOpen={userDetailOpen}
        onClose={() => setUserDetailOpen(false)}
        onUpdate={updateUserStatus}
        onSaveDetails={updateUserDetails}
        canEdit={canManageUsers}
      />
      <AnnouncementModal isOpen={announcementModalOpen} onClose={() => setAnnouncementModalOpen(false)} onSubmit={createAnnouncement} />
      <EmailBroadcastModal isOpen={emailModalOpen} onClose={() => setEmailModalOpen(false)} onSubmit={sendEmailBroadcast} />
      <TicketResponseModal ticket={selectedTicket} isOpen={ticketModalOpen} onClose={() => setTicketModalOpen(false)} onSubmit={respondToTicket} />
      <CreateUserModal isOpen={createUserModalOpen} onClose={() => setCreateUserModalOpen(false)} onSubmit={createUser} />
      <AddAdminModal isOpen={addAdminModalOpen} onClose={() => setAddAdminModalOpen(false)} onSubmit={addAdminTeamMember} />
      <EditAdminModal 
        adminUser={selectedAdmin} 
        isOpen={editAdminModalOpen} 
        onClose={() => setEditAdminModalOpen(false)} 
        onUpdate={updateAdminTeamMember}
        onActivate={activateAdminTeamMember}
        onDeactivate={deactivateAdminTeamMember}
        onDelete={deleteAdminTeamMember}
      />
    </div>
  );
}
